import { ConvexHttpClient } from "convex/browser";
import { makeFunctionReference } from "convex/server";

// CRA inlines REACT_APP_* env vars at build time. On Vercel this is set by the
// `convex deploy --cmd-url-env-var-name REACT_APP_CONVEX_URL` build command;
// locally it comes from `.env.local`.
// Strip any trailing slash - ConvexHttpClient appends "/api/..." itself, so a
// trailing slash would produce a malformed "...cloud//api/..." request.
const CONVEX_URL = (process.env.REACT_APP_CONVEX_URL || "").trim().replace(/\/+$/, "");

let client = null;

/**
 * Returns a singleton ConvexHttpClient, or null if no URL is configured
 * (e.g. local-storage-only users). Callers fall back gracefully when null.
 */
export function getConvexClient() {
  if (!CONVEX_URL) return null;
  if (!client) client = new ConvexHttpClient(CONVEX_URL);
  return client;
}

export function isConvexConfigured() {
  return Boolean(CONVEX_URL);
}

// Function references are resolved by name so the frontend builds independently
// of Convex codegen (`convex/_generated`).
export const convexRefs = {
  // Session auth (passkey verified server-side; see convex/auth.ts).
  login: makeFunctionReference("auth:login"),
  me: makeFunctionReference("auth:me"),
  logout: makeFunctionReference("auth:logout"),
  logoutOthers: makeFunctionReference("auth:logoutOthers"),
  listSessions: makeFunctionReference("auth:listSessions"),
  getAll: makeFunctionReference("data:getAll"),
  saveAll: makeFunctionReference("data:saveAll"),
  ping: makeFunctionReference("data:ping"),
  listSnapshots: makeFunctionReference("data:listSnapshots"),
  getSnapshot: makeFunctionReference("data:getSnapshot"),
  restoreSnapshot: makeFunctionReference("data:restoreSnapshot"),
  restoreSnapshotSection: makeFunctionReference("data:restoreSnapshotSection"),
  // Record-level sync (delta write + delta pull).
  applyChanges: makeFunctionReference("data:applyChanges"),
  getChangesSince: makeFunctionReference("data:getChangesSince"),
  // Version history schedule + manual controls.
  saveSnapshotNow: makeFunctionReference("data:saveSnapshotNow"),
  clearSnapshots: makeFunctionReference("data:clearSnapshots"),
  getSnapshotPrefs: makeFunctionReference("data:getSnapshotPrefs"),
  setSnapshotPrefs: makeFunctionReference("data:setSnapshotPrefs"),
  // Passkey management (change / recovery codes / forgot passkey).
  passkeyStatus: makeFunctionReference("auth:passkeyStatus"),
  changePasskey: makeFunctionReference("auth:changePasskey"),
  regenerateRecoveryCodes: makeFunctionReference("auth:regenerateRecoveryCodes"),
  resetPasskeyWithRecovery: makeFunctionReference("auth:resetPasskeyWithRecovery"),
  // AI companion (keys are encrypted server-side; see convex/ai.ts).
  aiGetSettings: makeFunctionReference("aiData:getSettings"),
  aiSetSettings: makeFunctionReference("aiData:setSettings"),
  aiDeleteKey: makeFunctionReference("aiData:deleteKey"),
  aiUsage: makeFunctionReference("aiData:usage"),
  aiSaveKey: makeFunctionReference("ai:saveKey"),
  aiListModels: makeFunctionReference("ai:listModels"),
  aiTest: makeFunctionReference("ai:testConnection"),
  aiChat: makeFunctionReference("ai:chat"),
};
